import React, { useState, useEffect } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import moment from 'moment';
import { Table } from 'react-bootstrap';
import '@fortawesome/fontawesome-free/css/all.min.css';


const mapStyles = {
  width: '100%',
  height: '87vh',
};

const ConvertToTimeZone = (datetime, loc_id) => {
  const timezone = {
    '6': 'America/Denver',
    '20': 'America/Denver',
    '3': 'America/New_York',
    '5': 'America/New_York',
    '4': 'America/Chicago',
    '2': 'America/Toronto',
    '1': 'America/Edmonton',
    '7': 'America/Vancouver',
    '9': 'America/Toronto',
    '8': 'America/Toronto',
    '12': 'America/Toronto',
    '21': 'Asia/Dubai',
    '22': 'America/Phoenix',
    '23': 'America/New_York'
  };
  // const convertedTime = moment(datetime).tz(timezone[loc_id]).format('YYYY-MM-DD HH:mm:ss A');
  // return convertedTime;

  const convertedTime = moment.utc(datetime) // Treat the input as UTC
    .tz(timezone[loc_id])                    // Convert to target timezone
    .format('MM/DD/YYYY hh:mm:ss A');         // Format the output
  
  return convertedTime;
};

const formatDateRange = (startDate, endDate, loc_id) => {
  // Convert both start and end dates to the appropriate timezone
  const start = moment.utc(startDate).tz(getTimezone(loc_id));
  const end = moment.utc(endDate).tz(getTimezone(loc_id));

  // Check if both dates are on the same day
  const sameDay = start.isSame(end, 'day');

  if (sameDay) {
    // Format and return time range if dates are the same
    return `${start.format('MM/DD/YYYY')} ${start.format('hh:mm:ss A')} - ${end.format('hh:mm:ss A')}`;
  } else {
    // Format and return full date range if dates are different
    return `${start.format('MM/DD/YYYY hh:mm:ss A')} - ${end.format('MM/DD/YYYY hh:mm:ss A')}`;
  }
};

// Helper function to return timezone based on location ID
const getTimezone = (loc_id) => {
  const timezone = {
    '6': 'America/Denver',
    '20': 'America/Denver',
    '3': 'America/New_York',
    '5': 'America/New_York',
    '4': 'America/Chicago',
    '2': 'America/Toronto',
    '1': 'America/Edmonton',
    '7': 'America/Vancouver',
    '9': 'America/Toronto',
    '8': 'America/Toronto',
    '12': 'America/Toronto',
    '21': 'Asia/Dubai',
    '22': 'America/Phoenix',
    '23': 'America/New_York'
  };
  return timezone[loc_id] || 'UTC'; // Return default 'UTC' if loc_id is not found
};

// Define movingMarker outside the component to persist across renders
let movingMarker;

const ReportMap = ({ tripData }) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: 'AIzaSyC36o4niEA5vZG4bMS0CM5FBQaKEt6QADw', // Replace with your API key
  });

  const [map, setMap] = useState(null);
  const [defaultCenter, setDefaultCenter] = useState(null);
  const [defaultZoom, setDefaultZoom] = useState(10);
  const [activeTripIndexes, setActiveTripIndexes] = useState(new Set());
  const [directionsCache, setDirectionsCache] = useState({});
  const [directionsResponses, setDirectionsResponses] = useState([]);
  const [markers, setMarkers] = useState([]); 
  const [smallMarkersByTrip, setSmallMarkersByTrip] = useState({});
  const [infoWindow, setInfoWindow] = useState({ isVisible: false, position: null, content: '' });
  const [coordinateData, setCoordinateData] = useState([]); // Store coordinates and speed
  const [currentTripIndex, setCurrentTripIndex] = useState(null);
  const [completeTripStatus, setCompleteTripStatus] = useState(false);
  const [isShowAllSelected, setIsShowAllSelected] = useState(false);

  const squareIcon = {
    path: 'M -2 -2 L 2 -2 L 2 2 L -2 2 Z', // Simple square path
    fillColor: 'orange',
    fillOpacity: 1,
    strokeColor: 'orange',
    strokeWeight: 1,
    scale: 5,
  };

  const groupedTripData = tripData.trip;
  const completeTrip = tripData.complete_trip;

  const createSmallMarkers = (formattedData, tripIndex) => {
    const tripSmallMarkers = formattedData.map((point) => {
      const smallMarker = new window.google.maps.Marker({
        position: { lat: point.lat, lng: point.lng },
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 2,
          fillColor: 'black',
          fillOpacity: 1,
          strokeColor: 'blue',
          strokeWeight: 1,
        },
        map: map,
      });
  
      // smallMarker.addListener('mouseover', () => {
      //   const contentString = `
      //     <div style="width: 300px; height: 100px; display: flex; flex-direction: column;">
      //       <div><b>Coordinates: ${point.lat.toFixed(6)}, ${point.lng.toFixed(6)}</b></div>
      //       <div><b>Speed: ${point.speed || 'N/A'}</b></div>
      //       <div><b>Odometer: ${point.odometer}</b></div>
      //       <div><b>Updated Datetime: ${point.updateDatetime}</b></div>
      //     </div>
      //   `;
      smallMarker.addListener('click', () => {
        const contentString = `
          <div style="width: 300px; height: 100px; display: flex; flex-direction: column;">
            <div><b>Coordinates: ${point.lat.toFixed(6)}, ${point.lng.toFixed(6)}</b></div>
            <div><b>Speed: ${point.speed || 'N/A'}</b></div>
            <div><b>Odometer: ${point.odometer}</b></div>
            <div><b>Updated Datetime: ${point.updateDatetime}</b></div>
          </div>
        `;
        setInfoWindow({
          isVisible: true,
          position: { lat: point.lat, lng: point.lng },
          content: contentString,
        });
      });

      // smallMarker.addListener('mouseout', () => {
      //   setInfoWindow({ isVisible: false, position: null, content: '' });
      // });

      return smallMarker;
    });

    setSmallMarkersByTrip((prev) => ({
      ...prev,
      [tripIndex]: tripSmallMarkers,
    }));
  };

  function haversine(lat1, lon1, lat2, lon2) {
    const R = 6371000; // Radius of Earth in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;
  
    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  
    return R * c; // Distance in meters
  }

  const calculateRoute = (formattedData, tripIndex) => {
    if (directionsCache[tripIndex]) {
      createSmallMarkers(formattedData, tripIndex); // Create small markers when loading from cache
      return;
    }
    if (formattedData.length < 2) return;

    // Calculate the number of points to sample (max 25 points)
    const totalPoints = formattedData.length;
    const maxPoints = 25;
    const stepSize = Math.floor(totalPoints / maxPoints); // Calculate the step size

    // Select points evenly spaced out, ensuring we always include the first and last point
    let selectedPoints = [formattedData[0]]; // Start with the first point (origin)
    
    for (let i = 1; i < totalPoints - 1; i++) {
      if (i % stepSize === 0 && selectedPoints.length < maxPoints - 1) {
        selectedPoints.push(formattedData[i]); // Add intermediate point
      }
    }

    // Always include the last point (destination)
    selectedPoints.push(formattedData[totalPoints - 1]);

    const directionsService = new window.google.maps.DirectionsService();

    //======================= KEEP THIS TO CHECK THE COORDINATES ARE CORRECT OR NOT - START=====================
    // async function testWaypoints() {
    //   for (let i = 1; i < selectedPoints.length - 1; i++) {
    //     const waypoints = selectedPoints
    //       .slice(1, i + 1)
    //       .map(point => ({
    //         location: point,
    //         stopover: false,
    //       }));

    //     await new Promise((resolve) => {
    //       directionsService.route(
    //         {
    //           origin: selectedPoints[0],
    //           destination: selectedPoints[selectedPoints.length - 1],
    //           waypoints,
    //           travelMode: window.google.maps.TravelMode.DRIVING,
    //         },
    //         (result, status) => {
    //           console.log(
    //             `${waypoints.length} waypoints -> ${status}`,
    //             waypoints[waypoints.length - 1].location
    //           );
    //           resolve();
    //         }
    //       );
    //     });
    //   }
    // }

    // testWaypoints();

    // return; 
    //======================= KEEP THIS TO CHECK THE COORDINATES ARE CORRECT OR NOT - END=====================

    const origin = selectedPoints[0];
    const destination = selectedPoints[selectedPoints.length - 1];
    const waypoints = selectedPoints.slice(1, -1).map((point) => ({
      location: point,
      stopover: true,
    }));

    // TO CHECK ORIGIN AND DESTINATION IS CORRECT OR NOT
    // directionsService.route(
    //   {
    //     origin: selectedPoints[0],
    //     destination: selectedPoints[selectedPoints.length - 1],
    //     travelMode: window.google.maps.TravelMode.DRIVING,
    //   },
    //   (result, status) => {
    //     console.log("ORIGIN → DESTINATION ONLY:", status);
    //   }
    // );

    directionsService.route(
      {
        origin,
        destination,
        waypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
        optimizeWaypoints: false, // Correct placement of optimization flag
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirectionsCache((prev) => ({ ...prev, [tripIndex]: result }));
          createSmallMarkers(formattedData, tripIndex);
          const overviewPath = result.routes[0].overview_path;
          const originalPath = formattedData.map(point => ({
            lat: point.lat,
            lng: point.lng,
          }));

          // Function to check if a point lies between two points in a path
          // const isBetween = (point, start, end) => {
          //   const latBetween = point.lat >= Math.min(start.lat, end.lat) && point.lat <= Math.max(start.lat, end.lat);
          //   const lngBetween = point.lng >= Math.min(start.lng, end.lng) && point.lng <= Math.max(start.lng, end.lng);
          //   return latBetween && lngBetween;
          // };
          // let combinedPath = [];
          // for (let i = 0; i < originalPath.length - 1; i++) {
          //   const startPoint = originalPath[i];
          //   const endPoint = originalPath[i + 1];
          //   const filteredPoints = overviewPath.filter(overviewPoint => {
          //     const latOverview = overviewPoint.lat();  
          //     const lngOverview = overviewPoint.lng();  
          //     const point = { lat: latOverview, lng: lngOverview };
          //     return isBetween(point, startPoint, endPoint);
          //   });
          //   combinedPath = [...combinedPath, ...filteredPoints];
          //   combinedPath = [...combinedPath, startPoint];
          // }
          // combinedPath = [...combinedPath, originalPath[originalPath.length - 1]];

          const polyline = new window.google.maps.Polyline({
            path: originalPath,
            geodesic: true,
            strokeColor: 'blue',
            strokeOpacity: 0.8,
            strokeWeight: 3,
            icons: [
              {
                icon: {
                  path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                  scale: 3,
                  strokeColor: 'black',
                  strokeWeight: 1,
                },
                offset: '0%',
                repeat: '100px',
              },
            ],
            map: map,
          });

          setDirectionsResponses((prev) => [...prev, { tripIndex, polyline }]);
        } else {
          console.error(`Error fetching directions: ${status}`);
        }
      }
    );
  };

  const handleTripClick = (trip, index) => {
    if(index == 'c')
    {
      index = groupedTripData.length
      setCompleteTripStatus(true)
    }
    else if (index === 'h') {
      // Hide All Trips
      setCompleteTripStatus(false);
      // Remove all polylines from the map
      directionsResponses.forEach((response) => {
        response.polyline.setMap(null);
      });

      // Remove all small markers (blue dots)
      Object.values(smallMarkersByTrip).forEach((markers) => {
        markers.forEach((marker) => marker.setMap(null));
      });
      setSmallMarkersByTrip({});

      // Clear all markers, small markers, and trip indexes
      setDirectionsResponses([]);
      setMarkers([]);
      setActiveTripIndexes(new Set());

      // Remove moving marker
      if (movingMarker) {
        movingMarker.setMap(null);
        movingMarker = null;
      }

      // Remove play button from the map
      const playButton = document.getElementById('playPauseButton');
      if (playButton) {
        playButton.remove();
      }

      return; // Exit the function after hiding trips
    }
    const key = `trip-${index}`;
    const newActiveTripIndexes = new Set(activeTripIndexes);
    setCurrentTripIndex(index);
    if (newActiveTripIndexes.has(key)) {
      newActiveTripIndexes.delete(key);

      const polylineData = directionsResponses.find((response) => response.tripIndex === index);
      if (polylineData) {
        polylineData.polyline.setMap(null);
      }

      const smallMarkers = smallMarkersByTrip[index] || [];
      smallMarkers.forEach((marker) => marker.setMap(null));

      setDirectionsResponses((prev) => prev.filter((response) => response.tripIndex !== index));
      setMarkers((prev) => prev.filter((marker) => marker.tripIndex !== index));

      setSmallMarkersByTrip((prev) => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
    } 
    else {
      newActiveTripIndexes.add(key);

      const formattedData = trip.map((point) => ({
        lat: parseFloat(point.latitude),
        lng: parseFloat(point.longitude),
        speed: point.speed,
        odometer: point.odometer,
        updateDatetime: ConvertToTimeZone(point.geolocation_datetime, point.location_id),
      }));
      setCoordinateData(formattedData);

      if (directionsCache[index]) {
        const polyline = new window.google.maps.Polyline({
          path: directionsCache[index].routes[0].overview_path,
          geodesic: true,
          strokeColor: 'blue',
          strokeOpacity: 0.8,
          strokeWeight: 3,
          icons: [
            {
              icon: {
                path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                scale: 3,
                strokeColor: 'black',
                strokeWeight: 1,
              },
              offset: '0%',
              repeat: '100px',
            },
          ],
          map: map,
        });
        setDirectionsResponses((prev) => [...prev, { tripIndex: index, polyline }]);
        createSmallMarkers(formattedData, index);
      } else {
        calculateRoute(formattedData, index);
      }

      const startMarker = {
        position: formattedData[0],
        label: currentTripIndex === index ? 'Y' : 'S',
        tripIndex: index,
      };
      const endMarker = {
        position: formattedData[formattedData.length - 1],
        label: currentTripIndex === index ? 'X' : 'E',
        tripIndex: index,
      };
      setMarkers((prev) => [...prev, startMarker, endMarker]);

      const bounds = new window.google.maps.LatLngBounds();
      formattedData.forEach((point) => bounds.extend(point));
      map.fitBounds(bounds);
    }

    setActiveTripIndexes(newActiveTripIndexes);
  };

  //Play button functionality
  let isMoving = false; // To track whether the marker is moving
  let movementInterval = null; // Store interval ID
  let currentIndex = 0; // Track the current index for marker animation
  let buttonStatus = 'play'; // Tracks current button state

  const initializeMovingMarker = () => {
    if (!movingMarker && map) {
      movingMarker = new window.google.maps.Marker({
        position: { lat: completeTrip[0].latitude, lng: completeTrip[0].longitude },
        map: map,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 5,
          fillColor: 'red',
          fillOpacity: 1,
          strokeWeight: 2,
        },
      });
    }
  };

  const addPlayButtonToMap = () => {
    if (map) {
      const playButtonDiv = document.createElement('div');
      playButtonDiv.id = 'playPauseButton'; // Assign the ID here
      playButtonDiv.style.position = 'absolute';
      playButtonDiv.style.marginTop = '30px'; // Adjust margin to place below Map|Satellite button
      playButtonDiv.style.marginLeft = '10px'; // Adjust margin to place below Map|Satellite button
      playButtonDiv.style.zIndex = '1000';

      const playIcon = document.createElement('i');
      playIcon.classList.add('fas', 'fa-play');
      playIcon.style.fontSize = '24px';
      playIcon.style.cursor = 'pointer';
      playButtonDiv.appendChild(playIcon);
  
      // const playIcon = document.createElement('i');
      // playIcon.classList.add('fas', 'fa-play'); // Initially set as play icon
      // playIcon.style.fontSize = '24px';
      // playIcon.style.color = 'black';
      // playIcon.style.cursor = 'pointer';
      // playIcon.style.lineHeight = 'normal'; // Ensure consistent vertical alignment of the icon within the button
      // playIcon.style.display = 'inline-block'; // Ensure icon is inline with button

      // const playIcon = document.createElement('i');
      // playIcon.classList.add('fas', 'fa-play');
      // playIcon.style.fontSize = '24px';
      // playIcon.style.cursor = 'pointer';
      // playButtonDiv.appendChild(playIcon);

      // // Set the button size
      // playButtonDiv.style.padding = '10px 15px'; // Adjust button size for proper icon alignment
      // playButtonDiv.style.borderRadius = '5px'; // Rounded corners for the button
      // playButtonDiv.style.border = 'none'; // No border for the button
  
      // let buttonStatus = 'play'; // Tracks current icon state
      // Click event to toggle icon and marker movement
      playIcon.addEventListener('click', () => {
        if (buttonStatus === 'play') {
          // initializeMovingMarker();
          startMovingMarker();
          buttonStatus = 'pause';
          playIcon.classList.replace('fa-play', 'fa-pause'); // Switch to pause icon
        } else if (buttonStatus === 'pause') {
          pauseMovingMarker();
          // stopMovingMarker();
          buttonStatus = 'play';
          playIcon.classList.replace('fa-pause', 'fa-play'); // Switch to stop icon
        } else {
          stopMovingMarker();
          buttonStatus = 'play';
          playIcon.classList.replace('fa-stop', 'fa-play'); // Switch back to play icon
        }
      });
  
      // playButtonDiv.appendChild(playIcon);
      map.controls[window.google.maps.ControlPosition.LEFT_TOP].push(playButtonDiv);
    }
  };

  // Function to start moving the marker
  const startMovingMarker = () => {
    if (isMoving || currentIndex >= completeTrip.length) return;
    isMoving = true;
    // currentIndex = 0; // Start from the first coordinate
    // // Update the button to show the "Pause" icon
    // const playPauseButton = document.getElementById("playPauseButton");
    // if (playPauseButton) {
    //   playPauseButton.innerHTML = '<i class="fa fa-pause"></i>'; // Set to pause icon
    //   playPauseButton.title = "Pause";
    //   playPauseButton.style.fontSize = '24px';
    // }

    movementInterval = setInterval(() => {
      if (currentIndex < completeTrip.length) {
        // Update marker position with latitude and longitude
        const position = {
          lat: parseFloat(completeTrip[currentIndex].latitude),
          lng: parseFloat(completeTrip[currentIndex].longitude),
        };
        movingMarker.setPosition(position);
        if (map) {
          map.panTo(position);
          map.setZoom(15); // Set an appropriate zoom level for tracking
        }
        currentIndex++;
      } else {
        stopMovingMarker(); // Stop when all coordinates are traversed
      }
    }, 300); // Move the marker every half second (adjust as needed)
  };

  const pauseMovingMarker = () => {
    if (movementInterval) {
      clearInterval(movementInterval);
      movementInterval = null;
      isMoving = false;
    }
  };

  // Function to stop the marker
  const stopMovingMarker = () => {
    if (movementInterval) {
      clearInterval(movementInterval);
    }
    isMoving = false;
    currentIndex = 0;
    if (movingMarker) {
      movingMarker.setPosition({
        lat: parseFloat(completeTrip[0].latitude),
        lng: parseFloat(completeTrip[0].longitude),
      });
      map.panTo(movingMarker.getPosition());
    }
  };

  useEffect(() => {
    if (map && groupedTripData.length > 0) {
      const firstTripStartPoint = {
        lat: parseFloat(groupedTripData[0][0].latitude),
        lng: parseFloat(groupedTripData[0][0].longitude),
      };
      setDefaultCenter(firstTripStartPoint);
      setDefaultZoom(10);
      if(completeTripStatus)
      {
        movingMarker = new window.google.maps.Marker({
          position: { lat: parseFloat(completeTrip[0].latitude), lng: parseFloat(completeTrip[0].longitude) },
          map: map,
          title: "Moving Marker",
          animation: window.google.maps.Animation.DROP,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE, // Use circle shape for the marker
            scale: 10, // Size of the circle
            fillColor: "green", // Fill color (green)
            fillOpacity: 1, // Full opacity
            strokeColor: "white", // Optional: a white border around the circle
            strokeWeight: 2 // Optional: thickness of the border
          }
        });
        addPlayButtonToMap();
      }
    }
  }, [map, groupedTripData, completeTripStatus]);

  return isLoaded ? (
    <div style={{ display: 'flex' }}>
      <div style={{ width: '500px', height: '87vh', overflowY: 'scroll', borderRight: '1px solid #ccc' }}>
        <div 
          style={{
            display: 'flex', 
            justifyContent: 'space-between',
            cursor: 'pointer',
            padding: '15px',
            margin: '10px 0',
          }}
        >
          <button onClick={() => {
              handleTripClick(completeTrip, 'c');
              setIsShowAllSelected(true);
            }} 
            style={{
              backgroundColor: isShowAllSelected ? "blue" : "white",
              color: isShowAllSelected ? "white" : "blue",
              border: "2px solid blue",
              padding: "10px 15px",
              borderRadius: "5px",
              cursor: "pointer",
            }}>
            Show All Trips
          </button>
          <button onClick={() => {
              handleTripClick(completeTrip, "h");
              setIsShowAllSelected(false); // Reset Show All Trips button color
            }}
            style={{
              backgroundColor: "white",
              color: "black",
              border: "2px solid black",
              padding: "10px 15px",
              borderRadius: "5px",
              cursor: "pointer",
            }}>
            Hide All Trips
          </button>
        </div>
        {groupedTripData.map((trip, index) => (
          <div
            key={index}
            onClick={() => handleTripClick(trip[0].records, index)}
            style={{
              cursor: 'pointer',
              padding: '15px',
              margin: '10px 0',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              boxShadow: activeTripIndexes.has(`trip-${index}`)
                ? '0 4px 8px rgba(0, 0, 0, 0.15)'
                : '0 2px 4px rgba(0, 0, 0, 0.1)',
              backgroundColor: activeTripIndexes.has(`trip-${index}`)
                ? '#C6C7C9'
                : 'white',
            }}
          >
            <div style={{ display: 'flex',  alignItems: 'flex-start', position: 'relative' }}>
              {/* Shared Parent for First Circle and Dotted Line */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start', // Ensure items align properly
                  marginRight: '10px',
                }}
              >
                {/* Top Circle */}
                <div
                  style={{
                    width: '15px',
                    height: '15px',
                    backgroundColor: trip?.[0]?.type === "driving" ? 'white' : 'blue',
                    border: trip?.[0]?.type === "driving" ? '2px solid blue' : 'none',
                    borderRadius: '50%',
                  }}
                />
                {/* Dotted Line (Only if trip[1] exists) */}
                {trip[1] && (
                  <div
                    style={{
                      width: '2px',
                      height: '60px', // Adjust height to connect circles
                      borderLeft: '2px dotted black',
                      marginTop: '5px',
                    }}
                  />
                )}
              </div>
              <div style={{ flex: 2 }}>
                <h4 style={{ fontSize: '14px', color: '#333', margin: 0 }}>
                  {trip[0].records[0]['from_address']}
                </h4>
              </div>
              {trip[0].type === 'driving' ? (
                <div style={{ flex: 1, fontSize: '12px', color: '#555' }}>
                  <div>
                    <strong>Driving</strong>{' '}
                    {formatDateRange(
                      trip[0].start_datetime,
                      trip[0].end_datetime,
                      trip[0].records[0]['location_id']
                    )}
                  </div>
                  <div><strong>Driving Duration:</strong> {trip[0].duration}</div>
                  <div><strong>Distance:</strong> {trip[0].odometer_difference}</div>
                </div>
              ): (
                <div style={{ flex: 1, fontSize: '12px', color: '#555' }}>
                  <div>
                    <strong>Stopped</strong>{' '}
                    {formatDateRange(
                      trip[0].start_datetime,
                      trip[0].end_datetime,
                      trip[0].records[0]['location_id']
                    )}
                  </div>
                  <div><strong>Stopped Duration:</strong> {trip[0].duration}</div>
                  <div><strong>Idling:</strong> {trip[0].idling_duration}</div>
                </div>
              )}
            
            </div>

            {trip[1] && (
              <div style={{ marginTop: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <div
                    style={{
                      width: '15px',
                      height: '15px',
                      backgroundColor: 'blue',
                      borderRadius: '50%',
                      marginRight: '10px',
                    }}
                  />
                  <div style={{ flex: 2 }}>
                    <h4 style={{ fontSize: '14px', color: '#333', margin: 0 }}>
                      {trip[0].records[0]['to_address']}
                    </h4>
                  </div>
                  <div style={{ flex: 1, fontSize: '12px', color: '#555' }}>
                    <div>
                      <strong>Stopped</strong>{' '}
                       {formatDateRange(
                          trip[1].start_datetime,
                          trip[1].end_datetime,
                          trip[1].records[0]['location_id']
                        )}
                    </div>
                    <div><strong>Stop Duration</strong> {trip[1].duration}</div>
                    <div><strong>Idling:</strong> {trip[1].idling_duration}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
  
      <div style={{ flex: '1' }}>
        <GoogleMap
          mapContainerStyle={mapStyles}
          zoom={defaultZoom}
          center={defaultCenter}
          onLoad={(mapInstance) => setMap(mapInstance)}
        >
          {markers.map((marker, index) => (
            <Marker key={index} position={marker.position} label={marker.label} />
          ))}
  
          {infoWindow.isVisible && (
            <>
              <InfoWindow 
                position={infoWindow.position} 
                onCloseClick={() => setInfoWindow({ isVisible: false, position: null, content: '' })}
              >
                <div dangerouslySetInnerHTML={{ __html: infoWindow.content }} />
              </InfoWindow>
              <style>
              {`
                .gm-ui-hover-effect {
                  transform: scale(0.7); /* Reduce size of close button */
                  top: -10px; /* Adjust position if needed */
                  right: -10px;
                }
              `}
            </style>
          </>
          )}
        </GoogleMap>
      </div>
    </div>
  ) : (
    <p>Loading...</p>
  );
  
};

export default ReportMap;
